import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import {
  EXCEL_WEEK_SCHEDULE,
  EXCEL_OPERATIONAL_MONTHS,
  formatWeekDisplayLabel,
  formatMonthDisplayLabel,
  getExcelOperationalMonthFilterOptions,
} from '../constants/excelWeeks.ts';
import { AmountDisplay } from '../components/AmountDisplay.tsx';
import { formatPreferCrores } from '../utils/currency.ts';
import {
  Loader2,
  RotateCcw,
  Printer,
  BarChart3,
  Target,
  Calendar,
  Building,
  ChevronDown,
  X,
  Filter,
  Search,
} from 'lucide-react';

interface MatrixRow {
  committeeName: string;
  leads: string;
  leaders?: string;
  heads?: string;
  ideasAllocated: number;
  reviewed: number;
  toBeReviewed: number;
  clearedWipNos: number;
  wipSavingsVal?: number;
  wipSavingsValCr: number;
  submittedToIcVal?: number;
  submittedToIcValCr: number;
  approvedByEcVal?: number;
  approvedByEcValCr: number;
  futurePotentialByEcVal?: number;
  futurePotentialByEcValCr?: number;
  wtdAchieved?: number;
  wtdAchievedCr: number;
  mtdAchieved?: number;
  mtdAchievedCr: number;
  ytdAchieved?: number;
  ytdAchievedCr: number;
  weekTarget: number;
  monthTarget: number;
  yearTarget: number;
  wtdPct: number;
  mtdPct: number;
  yearAchdPct: number;
}

interface Dom680Data {
  overallKpis: {
    ideasAllocated: number;
    ideasReviewed: number;
    toBeReviewed: number;
    clearedWipNos: number;
    wipSavingsVal?: number;
    wipSavingsValCr: number;
    submittedToIcVal?: number;
    submittedToIcValCr: number;
    approvedByEcVal?: number;
    approvedByEcValCr: number;
    futurePotentialByEcVal?: number;
    futurePotentialByEcValCr?: number;
    wtd: { pct: number; achievedVal?: number; achievedCr: number; targetVal?: number; targetCr: number };
    mtd: { pct: number; achievedVal?: number; achievedCr: number; targetVal?: number; targetCr: number };
    ytd: { pct: number; achievedVal?: number; achievedCr: number; targetVal?: number; targetCr: number };
  };
  matrixRows: MatrixRow[];
  matrixTotals: MatrixRow;
}

const WEEK_OPTIONS = EXCEL_WEEK_SCHEDULE.map(w => ({
  label: w.formattedLabel,
  value: `${w.startDate}|${w.endDate}`,
}));

const MONTH_OPTIONS = getExcelOperationalMonthFilterOptions();

const YEAR_OPTIONS = [
  { label: 'Fiscal Year 2026 – 2027 (Apr 2026 – Mar 2027)', value: '2026-04-01|2027-03-31' },
  { label: 'Fiscal Year 2025 – 2026 (Apr 2025 – Mar 2026)', value: '2025-04-01|2026-03-31' },
  { label: 'Calendar Year 2026 (Jan – Dec 2026)', value: '2026-01-01|2026-12-31' },
  { label: 'Calendar Year 2027 (Jan – Dec 2027)', value: '2027-01-01|2027-12-31' },
];

/** Display-only: max 4 digits then ... (e.g. 54000025 → 5400...%) */
function formatPctDisplay(pct: number | null | undefined): string {
  const n = Math.round(Number(pct) || 0);
  const digits = String(Math.abs(n));
  if (digits.length <= 4) return `${n}%`;
  const truncated = digits.slice(0, 4);
  return n < 0 ? `-${truncated}...%` : `${truncated}...%`;
}

export function Dom680ReportPage() {
  const navigate = useNavigate();
  const isAdmin = localStorage.getItem('userRole') === 'ADMIN';
  const [activeTab, setActiveTab] = useState<'OVERALL' | 'MATRIX' | 'ACHIEVEMENT'>('OVERALL');
  const [isTransposed, setIsTransposed] = useState(false);

  // Filter States
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedCommittees, setSelectedCommittees] = useState<string[]>([]);
  const selectedCommittee = selectedCommittees.length === 1 ? selectedCommittees[0] : '';
  const [targetFrequencyFilter, setTargetFrequencyFilter] = useState<'ALL' | 'WEEKLY' | 'MONTHLY' | 'YEARLY'>('ALL');
  
  // Multi-Select States
  const [selectedWeeks, setSelectedWeeks] = useState<string[]>([]);
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<string[]>([]);

  const [isWeekDropdownOpen, setIsWeekDropdownOpen] = useState(false);
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);

  // Fetch configured target cycles registry to populate filter options dynamically from Target Settings
  const { data: savedTargetConfigs } = useQuery<any[]>({
    queryKey: ['dom680TargetConfigsList'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/reports/dom680-target-configs-list');
      return res.data.data;
    },
  });

  const dynamicWeekOptions = useMemo(() => {
    if (savedTargetConfigs && savedTargetConfigs.length > 0) {
      const weeklyCycles = savedTargetConfigs.filter((c: any) => c.targetFrequency === 'WEEKLY' && c.weeklyStartDate && c.weeklyEndDate);
      if (weeklyCycles.length > 0) {
        const map = new Map<string, { label: string; value: string }>();
        for (const c of weeklyCycles) {
          const s = c.weeklyStartDate.substring(0, 10);
          const e = c.weeklyEndDate.substring(0, 10);
          const val = `${s}|${e}`;
          const label = formatWeekDisplayLabel(s, e, c.weekLabel);
          if (!map.has(val)) {
            map.set(val, { label, value: val });
          }
        }
        return Array.from(map.values());
      }
    }
    return WEEK_OPTIONS;
  }, [savedTargetConfigs]);

  const dynamicMonthOptions = useMemo(() => {
    if (savedTargetConfigs && savedTargetConfigs.length > 0) {
      const monthlyCycles = savedTargetConfigs.filter((c: any) => c.targetFrequency === 'MONTHLY');
      if (monthlyCycles.length > 0) {
        const map = new Map<string, { label: string; value: string }>();
        for (const c of monthlyCycles) {
          const month = c.startMonth as number;
          const year = c.startYear as number;
          if (!month || !year) continue;
          const excel = EXCEL_OPERATIONAL_MONTHS.find((m) => m.month === month && m.year === year);
          const value = excel
            ? `${excel.start}|${excel.end}`
            : (() => {
                const mStr = String(month).padStart(2, '0');
                const lastDay = new Date(year, month, 0).getDate();
                return `${year}-${mStr}-01|${year}-${mStr}-${String(lastDay).padStart(2, '0')}`;
              })();
          if (!map.has(value)) {
            map.set(value, {
              label: formatMonthDisplayLabel(year, month),
              value,
            });
          }
        }
        if (map.size > 0) {
          return Array.from(map.values()).sort((a, b) => a.value.localeCompare(b.value));
        }
      }
    }
    // No monthly targets configured yet — keep Excel sheet months so filters still work
    return MONTH_OPTIONS;
  }, [savedTargetConfigs]);

  const dynamicYearOptions = useMemo(() => {
    if (savedTargetConfigs && savedTargetConfigs.length > 0) {
      const yearlyCycles = savedTargetConfigs.filter((c: any) => c.targetFrequency === 'YEARLY');
      if (yearlyCycles.length > 0) {
        const map = new Map<string, { label: string; value: string }>();
        for (const c of yearlyCycles) {
          const sM = String(c.startMonth || 4).padStart(2, '0');
          const eM = String(c.endMonth || 3).padStart(2, '0');
          const sY = c.startYear || c.year;
          const eY = c.endYear || (c.year + 1);
          const s = `${sY}-${sM}-01`;
          const lastDay = new Date(eY, c.endMonth || 3, 0).getDate();
          const e = `${eY}-${eM}-${String(lastDay).padStart(2, '0')}`;
          const val = `${s}|${e}`;
          const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const label = `Fiscal Year ${sY} – ${eY} (${MONTH_SHORT[(c.startMonth || 4) - 1]} ${sY} – ${MONTH_SHORT[(c.endMonth || 3) - 1]} ${eY})`;
          if (!map.has(val)) {
            map.set(val, { label, value: val });
          }
        }
        if (map.size > 0) return Array.from(map.values());
      }
    }
    return YEAR_OPTIONS;
  }, [savedTargetConfigs]);

  const committeeFilterRef = useRef<HTMLDivElement>(null);
  const weekFilterRef = useRef<HTMLDivElement>(null);
  const monthFilterRef = useRef<HTMLDivElement>(null);
  const yearFilterRef = useRef<HTMLDivElement>(null);

  const [isCommitteeDropdownOpen, setIsCommitteeDropdownOpen] = useState(false);
  const [committeeSearch, setCommitteeSearch] = useState('');
  const [weekSearch, setWeekSearch] = useState('');
  const [monthSearch, setMonthSearch] = useState('');
  const [yearSearch, setYearSearch] = useState('');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      if (committeeFilterRef.current && !committeeFilterRef.current.contains(targetNode)) {
        setIsCommitteeDropdownOpen(false);
      }
      if (weekFilterRef.current && !weekFilterRef.current.contains(targetNode)) {
        setIsWeekDropdownOpen(false);
      }
      if (monthFilterRef.current && !monthFilterRef.current.contains(targetNode)) {
        setIsMonthDropdownOpen(false);
      }
      if (yearFilterRef.current && !yearFilterRef.current.contains(targetNode)) {
        setIsYearDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleMultiSelectChange = (newSelected: string[]) => {
    if (newSelected.length === 0) {
      setStartDate('');
      setEndDate('');
      return;
    }
    const starts: string[] = [];
    const ends: string[] = [];
    for (const item of newSelected) {
      const [s, e] = item.split('|');
      if (s) starts.push(s);
      if (e) ends.push(e);
    }
    starts.sort();
    ends.sort();
    setStartDate(starts[0]);
    setEndDate(ends[ends.length - 1]);
  };

  const { data: departmentGroupsData } = useQuery({
    queryKey: ['department-groups'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/department-groups');
      return res.data.data || [];
    }
  });

  const allCommittees = Array.from(new Set<string>(
    (departmentGroupsData || []).map((g: any) => g.name)
  )).sort();

  const getMinMaxDatesFromSelection = (selection: string[]) => {
    if (!selection || selection.length === 0) return { startDate: '', endDate: '' };
    const starts: string[] = [];
    const ends: string[] = [];
    for (const item of selection) {
      const [s, e] = item.split('|');
      if (s) starts.push(s);
      if (e) ends.push(e);
    }
    if (starts.length === 0 || ends.length === 0) return { startDate: '', endDate: '' };
    starts.sort();
    ends.sort();
    return {
      startDate: starts[0],
      endDate: ends[ends.length - 1],
    };
  };

  const weekRange = useMemo(() => getMinMaxDatesFromSelection(selectedWeeks), [selectedWeeks]);
  const monthRange = useMemo(() => getMinMaxDatesFromSelection(selectedMonths), [selectedMonths]);
  const yearRange = useMemo(() => getMinMaxDatesFromSelection(selectedYears), [selectedYears]);

  const { data: reportData, isLoading } = useQuery<Dom680Data>({
    queryKey: [
      'dom680TrackerReport',
      selectedCommittees,
      targetFrequencyFilter,
      weekRange.startDate,
      weekRange.endDate,
      monthRange.startDate,
      monthRange.endDate,
      yearRange.startDate,
      yearRange.endDate,
    ],
    queryFn: async () => {
      const res = await apiClient.get('/admin/reports/dom680-tracker', {
        params: {
          committee: selectedCommittees.length > 0 ? selectedCommittees.join('|') : undefined,
          targetFrequency: targetFrequencyFilter !== 'ALL' ? targetFrequencyFilter : undefined,
          weekStartDate: weekRange.startDate || undefined,
          weekEndDate: weekRange.endDate || undefined,
          monthStartDate: monthRange.startDate || undefined,
          monthEndDate: monthRange.endDate || undefined,
          yearStartDate: yearRange.startDate || undefined,
          yearEndDate: yearRange.endDate || undefined,
        }
      });
      return res.data.data;
    },
  });

  const scrollToSection = (sectionId: string, tabName: 'OVERALL' | 'MATRIX' | 'ACHIEVEMENT') => {
    setActiveTab(tabName);
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const kpis = reportData?.overallKpis;
  const matrixRows = reportData?.matrixRows || [];
  const matrixTotals = reportData?.matrixTotals;

  return (
    <Layout title="DOM 680 DASHBOARD">
      {/* Equal tight section gaps; sticky opaque strip covers main top padding (no scroll peek) */}
      <div className="flex flex-col gap-2">
        <div className="sticky top-0 z-40 bg-[#F4F7FA]">
          <div className="-mx-6 md:-mx-8 -mt-6 md:-mt-8 px-6 md:px-8 pt-1 pb-2 bg-[#F4F7FA]">
            <div className="bg-white rounded-2xl px-4 py-2.5 border border-slate-200 shadow-md flex flex-col md:flex-row md:items-center justify-between gap-2.5">
              <div className="flex items-center space-x-2.5 min-w-0">
                <div className="p-2 bg-slate-900 text-white rounded-xl shadow-md shrink-0">
                  <BarChart3 className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-base font-black tracking-tight text-slate-900">DOM 680</span>
                    <span className="bg-emerald-100 text-emerald-800 text-[10px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wider">Reports</span>
                  </div>
                  <p className="text-[11px] text-slate-500 leading-tight mt-0.5 truncate">High-level aggregated metrics across all operational committees</p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="bg-slate-100 p-0.5 rounded-xl flex items-center space-x-0.5 border border-slate-200">
                  <button
                    onClick={() => scrollToSection('overall-kpis', 'OVERALL')}
                    className={`px-3 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${activeTab === 'OVERALL'
                      ? 'bg-slate-900 text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                      }`}
                  >
                    Overall KPIs
                  </button>
                  <button
                    onClick={() => scrollToSection('committee-wise', 'MATRIX')}
                    className={`px-3 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${activeTab === 'MATRIX'
                      ? 'bg-slate-900 text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                      }`}
                  >
                    Committee Wise Tracker
                  </button>
                  <button
                    onClick={() => scrollToSection('ec-value-wise', 'ACHIEVEMENT')}
                    className={`px-3 py-1 rounded-lg text-[11px] font-bold transition-all cursor-pointer ${activeTab === 'ACHIEVEMENT'
                      ? 'bg-slate-900 text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                      }`}
                  >
                    EC Value Achievement
                  </button>
                </div>

                {isAdmin && (
                  <button
                    onClick={() => navigate('/admin/reports/dom680/targets')}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3 py-1.5 rounded-xl text-[11px] transition-all flex items-center space-x-1.5 cursor-pointer shadow-sm"
                  >
                    <Target className="w-3.5 h-3.5 text-emerald-100" />
                    <span>Target Settings</span>
                  </button>
                )}

                {/* Print / Export — commented out; uncomment to re-enable
                <button
                  onClick={handlePrint}
                  className="bg-slate-900 hover:bg-slate-800 text-white font-semibold px-3 py-1.5 rounded-xl text-[11px] transition-all flex items-center space-x-1.5 cursor-pointer shadow-sm"
                >
                  <Printer className="w-3.5 h-3.5 text-slate-300" />
                  <span>Export / Print</span>
                </button>
                */}
              </div>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white px-4 py-2.5 rounded-2xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-4">
            {/* 1. COMMITTEE Filter Dropdown (Multi-Select) */}
            <div ref={committeeFilterRef} className="relative">
              <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1 flex items-center space-x-1">
                <Building className="w-3 h-3 text-slate-400" />
                <span>Committee</span>
              </span>
              <button
                type="button"
                onClick={() => {
                  setIsCommitteeDropdownOpen(!isCommitteeDropdownOpen);
                  setIsWeekDropdownOpen(false);
                  setIsMonthDropdownOpen(false);
                  setIsYearDropdownOpen(false);
                }}
                className={`border rounded-xl px-3.5 py-1.5 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-slate-900 cursor-pointer shadow-sm flex items-center justify-between space-x-2.5 min-w-[170px] transition-all ${
                  selectedCommittees.length > 0
                    ? 'border-slate-300 bg-slate-50 text-slate-900'
                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center space-x-2 truncate">
                  <Filter className={`w-3.5 h-3.5 shrink-0 ${selectedCommittees.length > 0 ? 'text-slate-900' : 'text-slate-400'}`} />
                  <span className="truncate">
                    {selectedCommittees.length === 0
                      ? 'All Committees'
                      : selectedCommittees.length === 1
                      ? selectedCommittees[0]
                      : `${selectedCommittees.length} Committees Selected`}
                  </span>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              </button>

              {isCommitteeDropdownOpen && (
                <div className="absolute left-0 top-full mt-1.5 z-50 bg-white border border-slate-200 rounded-2xl shadow-2xl w-64 p-2.5 animate-in fade-in zoom-in-95 space-y-2">
                  {/* Search Bar */}
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                    <input
                      type="text"
                      value={committeeSearch}
                      onChange={(e) => setCommitteeSearch(e.target.value)}
                      placeholder="Search committees..."
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-8 pr-3 py-1.5 text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900"
                    />
                  </div>

                  {/* Select All Checkbox */}
                  <div className="px-2.5 py-1.5 border-b border-slate-100 flex items-center justify-between">
                    <div
                      onClick={() => {
                        const filteredVals = allCommittees
                          .filter(c => c.toLowerCase().includes(committeeSearch.toLowerCase()));
                        const isAllSelected = filteredVals.every(v => selectedCommittees.includes(v));
                        if (isAllSelected) {
                          setSelectedCommittees(selectedCommittees.filter(v => !filteredVals.includes(v)));
                        } else {
                          setSelectedCommittees(Array.from(new Set([...selectedCommittees, ...filteredVals])));
                        }
                      }}
                      className="flex items-center space-x-3 text-xs cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={
                          allCommittees.length > 0 &&
                          allCommittees.every(c => selectedCommittees.includes(c))
                        }
                        readOnly
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 accent-blue-600 focus:ring-blue-500 cursor-pointer pointer-events-none shrink-0"
                      />
                      <span className="font-extrabold text-slate-900 text-xs">Select All</span>
                    </div>
                    {selectedCommittees.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedCommittees([])}
                        className="text-[10px] font-bold text-rose-600 hover:underline cursor-pointer"
                      >
                        Clear Selection
                      </button>
                    )}
                  </div>

                  {/* Options List */}
                  <div className="max-h-52 overflow-y-auto space-y-0.5 pr-1">
                    {allCommittees
                      .filter(c => c.toLowerCase().includes(committeeSearch.toLowerCase()))
                      .map(c => {
                        const isChecked = selectedCommittees.includes(c);
                        return (
                          <div
                            key={c}
                            onClick={(e) => {
                              e.stopPropagation();
                              const next = isChecked
                                ? selectedCommittees.filter(v => v !== c)
                                : [...selectedCommittees, c];
                              setSelectedCommittees(next);
                            }}
                            className={`flex items-center space-x-3 px-2.5 py-1.5 rounded-xl text-xs cursor-pointer transition-colors ${
                              isChecked ? 'bg-slate-50 text-slate-900 font-bold' : 'text-slate-700 font-medium hover:bg-slate-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              readOnly
                              className="w-4 h-4 rounded border-slate-300 text-blue-600 accent-blue-600 focus:ring-blue-500 cursor-pointer shrink-0 pointer-events-none"
                            />
                            <span className={`truncate ${isChecked ? 'text-slate-900 font-bold' : 'text-slate-800 font-medium'}`}>{c}</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

            {/* 2. WEEKS Filter Dropdown */}
            <div ref={weekFilterRef} className="relative">
              <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1 flex items-center space-x-1">
                <Calendar className="w-3 h-3 text-slate-400" />
                <span>Weeks</span>
              </span>
              <button
                type="button"
                onClick={() => {
                  setIsWeekDropdownOpen(!isWeekDropdownOpen);
                  setIsCommitteeDropdownOpen(false);
                  setIsMonthDropdownOpen(false);
                  setIsYearDropdownOpen(false);
                }}
                className={`border rounded-xl px-3.5 py-1.5 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-slate-900 cursor-pointer shadow-sm flex items-center justify-between space-x-2.5 min-w-[180px] transition-all ${
                  selectedWeeks.length > 0
                    ? 'border-slate-300 bg-slate-50 text-slate-900'
                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center space-x-2 truncate">
                  <Filter className={`w-3.5 h-3.5 shrink-0 ${selectedWeeks.length > 0 ? 'text-slate-900' : 'text-slate-400'}`} />
                  <span className="truncate">
                    {selectedWeeks.length === 0
                      ? 'Select Weeks...'
                      : selectedWeeks.length === 1
                      ? dynamicWeekOptions.find(o => o.value === selectedWeeks[0])?.label
                      : `${selectedWeeks.length} Weeks Selected`}
                  </span>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              </button>

              {isWeekDropdownOpen && (
                <div className="absolute left-0 top-full mt-1.5 z-50 bg-white border border-slate-200 rounded-2xl shadow-2xl w-80 p-2.5 animate-in fade-in zoom-in-95 space-y-2">
                  {/* Search Bar */}
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                    <input
                      type="text"
                      value={weekSearch}
                      onChange={(e) => setWeekSearch(e.target.value)}
                      placeholder="Search weeks..."
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-8 pr-3 py-1.5 text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900"
                    />
                  </div>

                  {/* Select All Checkbox */}
                  <div className="px-2.5 py-1.5 border-b border-slate-100 flex items-center justify-between">
                    <div
                      onClick={() => {
                        const filteredVals = dynamicWeekOptions
                          .filter(o => o.label.toLowerCase().includes(weekSearch.toLowerCase()))
                          .map(o => o.value);
                        const isAllSelected = filteredVals.every(v => selectedWeeks.includes(v));
                        if (isAllSelected) {
                          setSelectedWeeks(selectedWeeks.filter(v => !filteredVals.includes(v)));
                        } else {
                          setSelectedWeeks(Array.from(new Set([...selectedWeeks, ...filteredVals])));
                        }
                      }}
                      className="flex items-center space-x-3 text-xs cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={
                          dynamicWeekOptions.length > 0 &&
                          dynamicWeekOptions.every(o => selectedWeeks.includes(o.value))
                        }
                        readOnly
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 accent-blue-600 focus:ring-blue-500 cursor-pointer pointer-events-none shrink-0"
                      />
                      <span className="font-extrabold text-slate-900 text-xs">Select All</span>
                    </div>
                    {selectedWeeks.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedWeeks([])}
                        className="text-[10px] font-bold text-rose-600 hover:underline cursor-pointer"
                      >
                        Clear Selection
                      </button>
                    )}
                  </div>

                  {/* Options List */}
                  <div className="max-h-56 overflow-y-auto space-y-0.5 pr-1">
                    {dynamicWeekOptions
                      .filter(opt => opt.label.toLowerCase().includes(weekSearch.toLowerCase()))
                      .map((opt) => {
                        const isChecked = selectedWeeks.includes(opt.value);
                        return (
                          <div
                            key={opt.value}
                            onClick={(e) => {
                              e.stopPropagation();
                              const next = isChecked
                                ? selectedWeeks.filter(v => v !== opt.value)
                                : [...selectedWeeks, opt.value];
                              setSelectedWeeks(next);
                            }}
                            className={`flex items-center space-x-3 px-2.5 py-1.5 rounded-xl text-xs cursor-pointer transition-colors ${
                              isChecked ? 'bg-slate-50 text-slate-900 font-bold' : 'text-slate-700 font-medium hover:bg-slate-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              readOnly
                              className="w-4 h-4 rounded border-slate-300 text-blue-600 accent-blue-600 focus:ring-blue-500 cursor-pointer shrink-0 pointer-events-none"
                            />
                            <span className={`truncate ${isChecked ? 'text-slate-900 font-bold' : 'text-slate-800 font-medium'}`}>{opt.label}</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

            {/* 3. MONTHS Filter Dropdown */}
            <div ref={monthFilterRef} className="relative">
              <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1 flex items-center space-x-1">
                <Calendar className="w-3 h-3 text-slate-400" />
                <span>Months</span>
              </span>
              <button
                type="button"
                onClick={() => {
                  setIsMonthDropdownOpen(!isMonthDropdownOpen);
                  setIsCommitteeDropdownOpen(false);
                  setIsWeekDropdownOpen(false);
                  setIsYearDropdownOpen(false);
                }}
                className={`border rounded-xl px-3.5 py-1.5 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-slate-900 cursor-pointer shadow-sm flex items-center justify-between space-x-2.5 min-w-[180px] transition-all ${
                  selectedMonths.length > 0
                    ? 'border-slate-300 bg-slate-50 text-slate-900'
                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center space-x-2 truncate">
                  <Filter className={`w-3.5 h-3.5 shrink-0 ${selectedMonths.length > 0 ? 'text-slate-900' : 'text-slate-400'}`} />
                  <span className="truncate">
                    {selectedMonths.length === 0
                      ? 'Select Months...'
                      : selectedMonths.length === 1
                      ? dynamicMonthOptions.find(o => o.value === selectedMonths[0])?.label
                      : `${selectedMonths.length} Months Selected`}
                  </span>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              </button>

              {isMonthDropdownOpen && (
                <div className="absolute left-0 top-full mt-1.5 z-50 bg-white border border-slate-200 rounded-2xl shadow-2xl w-80 p-2.5 animate-in fade-in zoom-in-95 space-y-2">
                  {/* Search Bar */}
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                    <input
                      type="text"
                      value={monthSearch}
                      onChange={(e) => setMonthSearch(e.target.value)}
                      placeholder="Search months..."
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-8 pr-3 py-1.5 text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900"
                    />
                  </div>

                  {/* Select All Checkbox */}
                  <div className="px-2.5 py-1.5 border-b border-slate-100 flex items-center justify-between">
                    <div
                      onClick={() => {
                        const filteredVals = dynamicMonthOptions
                          .filter(o => o.label.toLowerCase().includes(monthSearch.toLowerCase()))
                          .map(o => o.value);
                        const isAllSelected = filteredVals.every(v => selectedMonths.includes(v));
                        if (isAllSelected) {
                          setSelectedMonths(selectedMonths.filter(v => !filteredVals.includes(v)));
                        } else {
                          setSelectedMonths(Array.from(new Set([...selectedMonths, ...filteredVals])));
                        }
                      }}
                      className="flex items-center space-x-3 text-xs cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={
                          dynamicMonthOptions.length > 0 &&
                          dynamicMonthOptions.every(o => selectedMonths.includes(o.value))
                        }
                        readOnly
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 accent-blue-600 focus:ring-blue-500 cursor-pointer pointer-events-none shrink-0"
                      />
                      <span className="font-extrabold text-slate-900 text-xs">Select All</span>
                    </div>
                    {selectedMonths.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedMonths([])}
                        className="text-[10px] font-bold text-rose-600 hover:underline cursor-pointer"
                      >
                        Clear Selection
                      </button>
                    )}
                  </div>

                  {/* Options List */}
                  <div className="max-h-56 overflow-y-auto space-y-0.5 pr-1">
                    {dynamicMonthOptions
                      .filter(opt => opt.label.toLowerCase().includes(monthSearch.toLowerCase()))
                      .map((opt) => {
                        const isChecked = selectedMonths.includes(opt.value);
                        return (
                          <div
                            key={opt.value}
                            onClick={(e) => {
                              e.stopPropagation();
                              const next = isChecked
                                ? selectedMonths.filter(v => v !== opt.value)
                                : [...selectedMonths, opt.value];
                              setSelectedMonths(next);
                            }}
                            className={`flex items-center space-x-3 px-2.5 py-1.5 rounded-xl text-xs cursor-pointer transition-colors ${
                              isChecked ? 'bg-slate-50 text-slate-900 font-bold' : 'text-slate-700 font-medium hover:bg-slate-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              readOnly
                              className="w-4 h-4 rounded border-slate-300 text-blue-600 accent-blue-600 focus:ring-blue-500 cursor-pointer shrink-0 pointer-events-none"
                            />
                            <span className={`truncate ${isChecked ? 'text-slate-900 font-bold' : 'text-slate-800 font-medium'}`}>{opt.label}</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

            {/* 4. YEARS Filter Dropdown (Commented out per user request)
            <div ref={yearFilterRef} className="relative">
              <span className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1 flex items-center space-x-1">
                <Calendar className="w-3 h-3 text-slate-400" />
                <span>Years</span>
              </span>
              <button
                type="button"
                onClick={() => {
                  setIsYearDropdownOpen(!isYearDropdownOpen);
                  setIsCommitteeDropdownOpen(false);
                  setIsWeekDropdownOpen(false);
                  setIsMonthDropdownOpen(false);
                }}
                className={`border rounded-xl px-3.5 py-1.5 text-xs font-bold focus:outline-none focus:ring-2 focus:ring-slate-900 cursor-pointer shadow-sm flex items-center justify-between space-x-2.5 min-w-[180px] transition-all ${
                  selectedYears.length > 0
                    ? 'border-slate-300 bg-slate-50 text-slate-900'
                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center space-x-2 truncate">
                  <Filter className={`w-3.5 h-3.5 shrink-0 ${selectedYears.length > 0 ? 'text-slate-900' : 'text-slate-400'}`} />
                  <span className="truncate">
                    {selectedYears.length === 0
                      ? 'Select Years...'
                      : selectedYears.length === 1
                      ? dynamicYearOptions.find(o => o.value === selectedYears[0])?.label.split(' (')[0]
                      : `${selectedYears.length} Years Selected`}
                  </span>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              </button>

              {isYearDropdownOpen && (
                <div className="absolute left-0 top-full mt-1.5 z-50 bg-white border border-slate-200 rounded-2xl shadow-2xl w-80 p-2.5 animate-in fade-in zoom-in-95 space-y-2">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-2.5" />
                    <input
                      type="text"
                      value={yearSearch}
                      onChange={(e) => setYearSearch(e.target.value)}
                      placeholder="Search years..."
                      className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-8 pr-3 py-1.5 text-xs font-semibold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900"
                    />
                  </div>

                  <div className="px-2.5 py-1.5 border-b border-slate-100 flex items-center justify-between">
                    <label
                      onClick={() => {
                        const filteredVals = dynamicYearOptions
                          .filter(o => o.label.toLowerCase().includes(yearSearch.toLowerCase()))
                          .map(o => o.value);
                        const isAllSelected = filteredVals.every(v => selectedYears.includes(v));
                        if (isAllSelected) {
                          setSelectedYears(selectedYears.filter(v => !filteredVals.includes(v)));
                        } else {
                          setSelectedYears(Array.from(new Set([...selectedYears, ...filteredVals])));
                        }
                      }}
                      className="flex items-center space-x-3 text-xs cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={
                          dynamicYearOptions.length > 0 &&
                          dynamicYearOptions.every(o => selectedYears.includes(o.value))
                        }
                        onChange={() => {}}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 accent-blue-600 focus:ring-blue-500 cursor-pointer"
                      />
                      <span className="font-extrabold text-slate-900 text-xs">Select All</span>
                    </label>
                    {selectedYears.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedYears([])}
                        className="text-[10px] font-bold text-rose-600 hover:underline cursor-pointer"
                      >
                        Clear Selection
                      </button>
                    )}
                  </div>

                  <div className="max-h-56 overflow-y-auto space-y-0.5 pr-1">
                    {dynamicYearOptions
                      .filter(opt => opt.label.toLowerCase().includes(yearSearch.toLowerCase()))
                      .map((opt) => {
                        const isChecked = selectedYears.includes(opt.value);
                        return (
                          <label
                            key={opt.value}
                            onClick={(e) => {
                              e.stopPropagation();
                              const next = isChecked
                                ? selectedYears.filter(v => v !== opt.value)
                                : [...selectedYears, opt.value];
                              setSelectedYears(next);
                            }}
                            className={`flex items-center space-x-3 px-2.5 py-1.5 rounded-xl text-xs cursor-pointer transition-colors ${
                              isChecked ? 'bg-slate-50 text-slate-900 font-bold' : 'text-slate-700 font-medium hover:bg-slate-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {}}
                              className="w-4 h-4 rounded border-slate-300 text-blue-600 accent-blue-600 focus:ring-blue-500 cursor-pointer shrink-0"
                            />
                            <span className={`truncate ${isChecked ? 'text-slate-900 font-bold' : 'text-slate-800 font-medium'}`}>{opt.label}</span>
                          </label>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
            */}

            {/* 3. Date Filters (Hidden / Commented out per user request)
            <div className="flex items-center space-x-2 border-l border-slate-200 pl-4">
              <Calendar className="w-4 h-4 text-slate-500 shrink-0" />
              <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">Date:</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border border-slate-200 rounded-xl px-2.5 py-1.5 text-xs bg-slate-50 text-slate-700 font-bold focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
              <span className="text-xs text-slate-400 font-medium">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border border-slate-200 rounded-xl px-2.5 py-1.5 text-xs bg-slate-50 text-slate-700 font-bold focus:outline-none focus:ring-2 focus:ring-emerald-500"
              />
            </div>
            */}
          </div>

          {(startDate || endDate || selectedCommittees.length > 0 || targetFrequencyFilter !== 'ALL' || selectedWeeks.length > 0 || selectedMonths.length > 0 || selectedYears.length > 0) && (
            <button
              onClick={() => {
                setStartDate('');
                setEndDate('');
                setSelectedCommittees([]);
                setTargetFrequencyFilter('ALL');
                setSelectedWeeks([]);
                setSelectedMonths([]);
                setSelectedYears([]);
                setIsCommitteeDropdownOpen(false);
                setIsWeekDropdownOpen(false);
                setIsMonthDropdownOpen(false);
                setIsYearDropdownOpen(false);
              }}
              className="text-xs font-medium text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-xl transition-all flex items-center space-x-1 cursor-pointer"
            >
              <X className="w-3.5 h-3.5" />
              <span>Clear Filters</span>
            </button>
          )}
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-200 shadow-sm">
            <Loader2 className="w-10 h-10 text-emerald-600 animate-spin mb-3" />
            <p className="text-sm font-semibold text-slate-600">Calculating DOM 680 Daily Metrics...</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {/* SECTION 1: OVERALL KPIS */}
            <div id="overall-kpis" className="scroll-mt-20">
              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
                <div className="mb-4 border-b border-slate-100 pb-3">
                  <h1 className="text-2xl font-black text-slate-900 tracking-tight">DOM 680 | DASHBOARD</h1>
                  <p className="text-slate-500 text-xs mt-1 font-medium">High-level aggregated metrics across all operational committees</p>
                </div>

                {/* KPI Cards Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                  {/* 1) Ideas Allocated */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 hover:border-slate-300 transition-all flex flex-col justify-start">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Ideas Allocated</p>
                    <p className="text-2xl font-black text-slate-900 mt-2">{kpis?.ideasAllocated.toLocaleString() || '0'}</p>
                  </div>

                  {/* 2) Ideas Reviewed */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 hover:border-slate-300 transition-all flex flex-col justify-start">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Ideas Reviewed</p>
                    <p className="text-2xl font-black text-slate-900 mt-2">{kpis?.ideasReviewed.toLocaleString() || '0'}</p>
                  </div>

                  {/* 3) To Be Reviewed */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 hover:border-slate-300 transition-all flex flex-col justify-start">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">To Be Reviewed</p>
                    <p className="text-2xl font-black text-slate-900 mt-2">{kpis?.toBeReviewed.toLocaleString() || '0'}</p>
                  </div>

                  {/* 4) Cleared & WIP (Nos) */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 hover:border-slate-300 transition-all flex flex-col justify-start">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Cleared & WIP (Nos)</p>
                    <p className="text-2xl font-black text-slate-900 mt-2">{kpis?.clearedWipNos.toLocaleString() || '0'}</p>
                  </div>

                  {/* 5) WIP Potential Savings */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 hover:border-slate-300 transition-all flex flex-col justify-start">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">WIP Potential Savings</p>
                    <div className="mt-2">
                      <AmountDisplay
                        amount={kpis?.wipSavingsVal ?? Math.round((kpis?.wipSavingsValCr || 0) * 10000000)}
                        label="WIP Potential Savings"
                        textSize="text-2xl"
                        forceCrores={true}
                      />
                    </div>
                  </div>

                  {/* 6) Submitted to EC */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 hover:border-slate-300 transition-all flex flex-col justify-start">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Submitted to EC</p>
                    <div className="mt-2">
                      <AmountDisplay
                        amount={kpis?.submittedToIcVal ?? Math.round((kpis?.submittedToIcValCr || 0) * 10000000)}
                        label="Submitted to EC Value"
                        textSize="text-2xl"
                        forceCrores={true}
                      />
                    </div>
                  </div>

                  {/* 7) Approved by EC */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 hover:border-slate-300 transition-all flex flex-col justify-start">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Approved by EC</p>
                    <div className="mt-2">
                      <AmountDisplay
                        amount={kpis?.approvedByEcVal ?? Math.round((kpis?.approvedByEcValCr || 0) * 10000000)}
                        label="Approved by EC Value"
                        textSize="text-2xl"
                        forceCrores={true}
                      />
                    </div>
                  </div>

                  {/* 7b) Future potential by EC */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 hover:border-slate-300 transition-all flex flex-col justify-start">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Future potential by EC</p>
                    <div className="mt-2">
                      <AmountDisplay
                        amount={
                          kpis?.futurePotentialByEcVal ??
                          Math.round((kpis?.futurePotentialByEcValCr || 0) * 10000000)
                        }
                        label="Future potential by EC"
                        textSize="text-2xl"
                        forceCrores={true}
                      />
                    </div>
                  </div>

                  {/* 8) WTD Achievement */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 hover:border-slate-300 transition-all flex flex-col justify-start min-w-0">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">WTD Achievement</p>
                    <div className="mt-2 min-w-0 space-y-0.5">
                      <div className="flex items-baseline justify-between gap-1.5 min-w-0 flex-nowrap">
                        <p className="text-xl font-black text-slate-900 truncate min-w-0" title={`${kpis?.wtd.pct || 0}%`}>
                          {formatPctDisplay(kpis?.wtd.pct)}
                        </p>
                        <div className="shrink-0">
                          <AmountDisplay
                            amount={kpis?.wtd.achievedVal ?? Math.round((kpis?.wtd.achievedCr || 0) * 10000000)}
                            label="WTD EC Approved Value"
                            textSize="text-sm font-bold"
                            forceCrores={true}
                          />
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-500 font-medium flex items-center gap-1 flex-nowrap min-w-0 overflow-hidden">
                        <span className="truncate">
                          {formatPreferCrores(kpis?.wtd.achievedVal ?? Math.round((kpis?.wtd.achievedCr || 0) * 10000000))} /
                        </span>
                        <span className="shrink-0">
                          <AmountDisplay
                            amount={kpis?.wtd.targetVal ?? Math.round((kpis?.wtd.targetCr || 0) * 10000000)}
                            label="WTD Target"
                            textSize="text-[11px] font-medium"
                            forceCrores={true}
                          />
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* 9) MTD Achievement */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 hover:border-slate-300 transition-all flex flex-col justify-start min-w-0">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">MTD Achievement</p>
                    <div className="mt-2 min-w-0 space-y-0.5">
                      <div className="flex items-baseline justify-between gap-1.5 min-w-0 flex-nowrap">
                        <p className="text-xl font-black text-slate-900 truncate min-w-0" title={`${kpis?.mtd.pct || 0}%`}>
                          {formatPctDisplay(kpis?.mtd.pct)}
                        </p>
                        <div className="shrink-0">
                          <AmountDisplay
                            amount={kpis?.mtd.achievedVal ?? Math.round((kpis?.mtd.achievedCr || 0) * 10000000)}
                            label="MTD EC Approved Value"
                            textSize="text-sm font-bold"
                            forceCrores={true}
                          />
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-500 font-medium flex items-center gap-1 flex-nowrap min-w-0 overflow-hidden">
                        <span className="truncate">
                          {formatPreferCrores(kpis?.mtd.achievedVal ?? Math.round((kpis?.mtd.achievedCr || 0) * 10000000))} /
                        </span>
                        <span className="shrink-0">
                          <AmountDisplay
                            amount={kpis?.mtd.targetVal ?? Math.round((kpis?.mtd.targetCr || 0) * 10000000)}
                            label="MTD Target"
                            textSize="text-[11px] font-medium"
                            forceCrores={true}
                          />
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* 10) YTD Achievement */}
                  <div className="bg-slate-50 p-4 rounded-xl border border-slate-200/80 hover:border-slate-300 transition-all flex flex-col justify-start min-w-0">
                    <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">YTD Achievement</p>
                    <div className="mt-2 min-w-0 space-y-0.5">
                      <div className="flex items-baseline justify-between gap-1.5 min-w-0 flex-nowrap">
                        <p className="text-xl font-black text-slate-900 truncate min-w-0" title={`${kpis?.ytd.pct || 0}%`}>
                          {formatPctDisplay(kpis?.ytd.pct)}
                        </p>
                        <div className="shrink-0">
                          <AmountDisplay
                            amount={kpis?.ytd.achievedVal ?? Math.round((kpis?.ytd.achievedCr || 0) * 10000000)}
                            label="YTD EC Approved Value"
                            textSize="text-sm font-bold"
                            forceCrores={true}
                          />
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-500 font-medium flex items-center gap-1 flex-nowrap min-w-0 overflow-hidden">
                        <span className="truncate">
                          {formatPreferCrores(kpis?.ytd.achievedVal ?? Math.round((kpis?.ytd.achievedCr || 0) * 10000000))} /
                        </span>
                        <span className="shrink-0">
                          <AmountDisplay
                            amount={kpis?.ytd.targetVal ?? Math.round((kpis?.ytd.targetCr || 0) * 10000000)}
                            label="YTD Target"
                            textSize="text-[11px] font-medium"
                            forceCrores={true}
                          />
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* SECTION 2: COMMITTEE-WISE TRACKER MATRIX */}
            <div id="committee-wise" className="scroll-mt-20">
              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 border-b border-slate-100 pb-3">
                  <div>
                    <h1 className="text-2xl font-black text-slate-900 tracking-tight">Committee-Wise KPI Metrix</h1>
                    <p className="text-slate-500 text-xs mt-1 font-medium">Detailed daily progress breakdown across committee heads</p>
                  </div>
                  <button
                    onClick={() => setIsTransposed(!isTransposed)}
                    className="bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold px-4 py-2 rounded-xl text-xs transition-all flex items-center space-x-2 border border-blue-200 cursor-pointer shadow-sm self-start sm:self-auto"
                  >
                    <RotateCcw className="w-4 h-4" />
                    <span>{isTransposed ? 'Reset Matrix' : '🔄 Transpose Matrix'}</span>
                  </button>
                </div>

                <div className="text-xs text-slate-500 font-medium mb-3 flex items-center justify-between">
                  <span>Current View: {isTransposed ? 'Metrics in Rows, Committees in Columns' : 'Committee & Lead in Rows (Total in Last Row)'}</span>
                </div>

                {/* Matrix Table */}
                <div className="overflow-x-auto border border-slate-200 rounded-2xl shadow-sm">
                  {!isTransposed ? (
                    /* Standard Layout: Committees in Rows */
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-900 text-white font-bold tracking-wider">
                          <th className="p-3.5 uppercase border-r border-slate-800">COMMITTEE / LEAD</th>
                          <th className="p-3.5 uppercase text-center border-r border-slate-800">Ideas Allocated</th>
                          <th className="p-3.5 uppercase text-center border-r border-slate-800">Reviewed</th>
                          <th className="p-3.5 uppercase text-center border-r border-slate-800">To Be Reviewed</th>
                          <th className="p-3.5 uppercase text-center border-r border-slate-800">Cleared & WIP (Nos)</th>
                          <th className="p-3.5 uppercase text-center border-r border-slate-800">WIP Savings (Val)</th>
                          <th className="p-3.5 uppercase text-center border-r border-slate-800">Submitted to EC</th>
                          <th className="p-3.5 uppercase text-center border-r border-slate-800">Approved by EC</th>
                          <th className="p-3.5 uppercase text-center">Future potential by EC</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white font-medium text-slate-700">
                        {matrixRows.map((row, idx) => (
                          <tr key={row.committeeName} className={idx % 2 === 0 ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/50 hover:bg-slate-50'}>
                            <td className="p-3.5 border-r border-slate-200 font-bold text-slate-900">
                              <div>{row.committeeName}</div>
                              {row.heads && (
                                <div className="text-[10px] font-bold text-blue-700 mt-0.5 uppercase tracking-wide">
                                  Heads: <span className="font-semibold text-slate-700">{row.heads}</span>
                                </div>
                              )}
                              {!row.leaders && !row.heads && row.leads && (
                                <div className="text-[10px] font-semibold text-blue-600 mt-0.5 uppercase tracking-wide">
                                  {row.leads}
                                </div>
                              )}
                            </td>
                            <td className="p-3.5 text-center border-r border-slate-200 font-bold">{row.ideasAllocated.toLocaleString()}</td>
                            <td className="p-3.5 text-center border-r border-slate-200">{row.reviewed.toLocaleString()}</td>
                            <td className="p-3.5 text-center border-r border-slate-200 text-amber-700 font-bold">{row.toBeReviewed.toLocaleString()}</td>
                            <td className="p-3.5 text-center border-r border-slate-200">{row.clearedWipNos.toLocaleString()}</td>
                            <td className="p-3.5 text-center border-r border-slate-200">
                              <AmountDisplay
                                amount={row.wipSavingsVal ?? Math.round((row.wipSavingsValCr || 0) * 10000000)}
                                label={`${row.committeeName} WIP Savings`}
                                textSize="text-xs font-semibold"
                              />
                            </td>
                            <td className="p-3.5 text-center border-r border-slate-200">
                              <AmountDisplay
                                amount={row.submittedToIcVal ?? Math.round((row.submittedToIcValCr || 0) * 10000000)}
                                label={`${row.committeeName} Submitted to EC`}
                                textSize="text-xs font-semibold"
                              />
                            </td>
                            <td className="p-3.5 text-center border-r border-slate-200 font-bold text-emerald-700">
                              <AmountDisplay
                                amount={row.approvedByEcVal ?? Math.round((row.approvedByEcValCr || 0) * 10000000)}
                                label={`${row.committeeName} Approved by EC`}
                                textSize="text-xs font-bold"
                              />
                            </td>
                            <td className="p-3.5 text-center font-bold text-amber-700">
                              <AmountDisplay
                                amount={
                                  row.futurePotentialByEcVal ??
                                  Math.round((row.futurePotentialByEcValCr || 0) * 10000000)
                                }
                                label={`${row.committeeName} Future potential by EC`}
                                textSize="text-xs font-bold"
                                forceCrores={true}
                              />
                            </td>
                          </tr>
                        ))}

                        {/* TOTAL Row */}
                        {matrixTotals && (
                          <tr className="bg-slate-100 text-slate-900 font-black border-t-2 border-slate-300">
                            <td className="p-3.5 border-r border-slate-300 uppercase tracking-wider text-blue-950">TOTAL</td>
                            <td className="p-3.5 text-center border-r border-slate-300 text-blue-600">{matrixTotals.ideasAllocated.toLocaleString()}</td>
                            <td className="p-3.5 text-center border-r border-slate-300 text-blue-600">{matrixTotals.reviewed.toLocaleString()}</td>
                            <td className="p-3.5 text-center border-r border-slate-300 text-amber-700">{matrixTotals.toBeReviewed.toLocaleString()}</td>
                            <td className="p-3.5 text-center border-r border-slate-300 text-blue-600">{matrixTotals.clearedWipNos.toLocaleString()}</td>
                            <td className="p-3.5 text-center border-r border-slate-300 text-blue-600">
                              <AmountDisplay
                                amount={matrixTotals.wipSavingsVal ?? Math.round((matrixTotals.wipSavingsValCr || 0) * 10000000)}
                                label="Total WIP Savings"
                                textSize="text-xs font-bold"
                              />
                            </td>
                            <td className="p-3.5 text-center border-r border-slate-300 text-blue-600">
                              <AmountDisplay
                                amount={matrixTotals.submittedToIcVal ?? Math.round((matrixTotals.submittedToIcValCr || 0) * 10000000)}
                                label="Total Submitted to EC"
                                textSize="text-xs font-bold"
                              />
                            </td>
                            <td className="p-3.5 text-center border-r border-slate-300 text-emerald-700 font-black">
                              <AmountDisplay
                                amount={matrixTotals.approvedByEcVal ?? Math.round((matrixTotals.approvedByEcValCr || 0) * 10000000)}
                                label="Total Approved by EC"
                                textSize="text-xs font-black text-emerald-700"
                              />
                            </td>
                            <td className="p-3.5 text-center text-amber-700 font-black">
                              <AmountDisplay
                                amount={
                                  matrixTotals.futurePotentialByEcVal ??
                                  Math.round((matrixTotals.futurePotentialByEcValCr || 0) * 10000000)
                                }
                                label="Total Future potential by EC"
                                textSize="text-xs font-black text-amber-700"
                                forceCrores={true}
                              />
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  ) : (
                    /* Transposed Layout: Metrics in Rows, Committees in Columns */
                    <table className="w-full text-left text-xs border-collapse">
                      <thead>
                        <tr className="bg-slate-900 text-white font-bold tracking-wider">
                          <th className="p-3.5 uppercase border-r border-slate-800">METRIC</th>
                          {matrixRows.map(r => (
                            <th key={r.committeeName} className="p-3.5 uppercase text-center border-r border-slate-800">{r.committeeName}</th>
                          ))}
                          <th className="p-3.5 uppercase text-center bg-slate-950 text-emerald-400">TOTAL</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 bg-white font-medium text-slate-700">
                        <tr>
                          <td className="p-3.5 border-r border-slate-200 font-bold bg-slate-50">Ideas Allocated</td>
                          {matrixRows.map(r => (
                            <td key={r.committeeName} className="p-3.5 text-center border-r border-slate-200 font-bold">{r.ideasAllocated}</td>
                          ))}
                          <td className="p-3.5 text-center font-black text-blue-600 bg-slate-100">{matrixTotals?.ideasAllocated}</td>
                        </tr>
                        <tr>
                          <td className="p-3.5 border-r border-slate-200 font-bold bg-slate-50">Reviewed</td>
                          {matrixRows.map(r => (
                            <td key={r.committeeName} className="p-3.5 text-center border-r border-slate-200">{r.reviewed}</td>
                          ))}
                          <td className="p-3.5 text-center font-black text-blue-600 bg-slate-100">{matrixTotals?.reviewed}</td>
                        </tr>
                        <tr>
                          <td className="p-3.5 border-r border-slate-200 font-bold bg-slate-50">To Be Reviewed</td>
                          {matrixRows.map(r => (
                            <td key={r.committeeName} className="p-3.5 text-center border-r border-slate-200 text-amber-700 font-bold">{r.toBeReviewed}</td>
                          ))}
                          <td className="p-3.5 text-center font-black text-amber-700 bg-slate-100">{matrixTotals?.toBeReviewed}</td>
                        </tr>
                        <tr>
                          <td className="p-3.5 border-r border-slate-200 font-bold bg-slate-50">Cleared & WIP (Nos)</td>
                          {matrixRows.map(r => (
                            <td key={r.committeeName} className="p-3.5 text-center border-r border-slate-200">{r.clearedWipNos}</td>
                          ))}
                          <td className="p-3.5 text-center font-black text-blue-600 bg-slate-100">{matrixTotals?.clearedWipNos}</td>
                        </tr>
                        <tr>
                          <td className="p-3.5 border-r border-slate-200 font-bold bg-slate-50">WIP Savings (Val)</td>
                          {matrixRows.map(r => (
                            <td key={r.committeeName} className="p-3.5 text-center border-r border-slate-200">
                              <AmountDisplay
                                amount={r.wipSavingsVal ?? Math.round((r.wipSavingsValCr || 0) * 10000000)}
                                label={`${r.committeeName} WIP Savings`}
                                textSize="text-xs font-semibold"
                              />
                            </td>
                          ))}
                          <td className="p-3.5 text-center font-black text-blue-600 bg-slate-100">
                            <AmountDisplay
                              amount={matrixTotals?.wipSavingsVal ?? Math.round((matrixTotals?.wipSavingsValCr || 0) * 10000000)}
                              label="Total WIP Savings"
                              textSize="text-xs font-bold"
                            />
                          </td>
                        </tr>
                        <tr>
                          <td className="p-3.5 border-r border-slate-200 font-bold bg-slate-50">Submitted to EC</td>
                          {matrixRows.map(r => (
                            <td key={r.committeeName} className="p-3.5 text-center border-r border-slate-200">
                              <AmountDisplay
                                amount={r.submittedToIcVal ?? Math.round((r.submittedToIcValCr || 0) * 10000000)}
                                label={`${r.committeeName} Submitted to EC`}
                                textSize="text-xs font-semibold"
                              />
                            </td>
                          ))}
                          <td className="p-3.5 text-center font-black text-blue-600 bg-slate-100">
                            <AmountDisplay
                              amount={matrixTotals?.submittedToIcVal ?? Math.round((matrixTotals?.submittedToIcValCr || 0) * 10000000)}
                              label="Total Submitted to EC"
                              textSize="text-xs font-bold"
                            />
                          </td>
                        </tr>
                        <tr>
                          <td className="p-3.5 border-r border-slate-200 font-bold bg-slate-50">Approved by EC</td>
                          {matrixRows.map(r => (
                            <td key={r.committeeName} className="p-3.5 text-center border-r border-slate-200 font-bold text-emerald-700">
                              <AmountDisplay
                                amount={r.approvedByEcVal ?? Math.round((r.approvedByEcValCr || 0) * 10000000)}
                                label={`${r.committeeName} Approved by EC`}
                                textSize="text-xs font-bold"
                              />
                            </td>
                          ))}
                          <td className="p-3.5 text-center font-black text-emerald-700 bg-slate-100">
                            <AmountDisplay
                              amount={matrixTotals?.approvedByEcVal ?? Math.round((matrixTotals?.approvedByEcValCr || 0) * 10000000)}
                              label="Total Approved by EC"
                              textSize="text-xs font-black text-emerald-700"
                            />
                          </td>
                        </tr>
                        <tr>
                          <td className="p-3.5 border-r border-slate-200 font-bold bg-slate-50">Future potential by EC</td>
                          {matrixRows.map(r => (
                            <td key={r.committeeName} className="p-3.5 text-center border-r border-slate-200 font-bold text-amber-700">
                              <AmountDisplay
                                amount={
                                  r.futurePotentialByEcVal ??
                                  Math.round((r.futurePotentialByEcValCr || 0) * 10000000)
                                }
                                label={`${r.committeeName} Future potential by EC`}
                                textSize="text-xs font-bold"
                                forceCrores={true}
                              />
                            </td>
                          ))}
                          <td className="p-3.5 text-center font-black text-amber-700 bg-slate-100">
                            <AmountDisplay
                              amount={
                                matrixTotals?.futurePotentialByEcVal ??
                                Math.round((matrixTotals?.futurePotentialByEcValCr || 0) * 10000000)
                              }
                              label="Total Future potential by EC"
                              textSize="text-xs font-black text-amber-700"
                              forceCrores={true}
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>

            {/* SECTION 3: EC VALUE ACHIEVEMENT (WTD / MTD / YTD) */}
            <div id="ec-value-wise" className="scroll-mt-20">
              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
                <div className="mb-4 border-b border-slate-100 pb-3">
                  <h1 className="text-2xl font-black text-slate-900 tracking-tight">EC Value Achievement — WTD / MTD / YTD</h1>
                  <p className="text-slate-500 text-xs mt-1 font-medium">Committee-wise EC approved value vs Weekly, Monthly & Yearly targets</p>
                </div>

                {/* Achievement Table */}
                <div className="overflow-x-auto border border-slate-200 rounded-2xl shadow-sm">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-slate-900 text-white font-bold tracking-wider">
                        <th className="p-3.5 uppercase border-r border-slate-800 w-[16%]">COMMITTEE</th>
                        {/* Weekly Group: Target -> Achieved -> % */}
                        <th className="p-3.5 uppercase text-center border-r border-slate-800 bg-slate-900">WEEK<br />TARGET</th>
                        <th className="p-3.5 uppercase text-center border-r border-slate-800 bg-slate-900">EC (VALUE)<br />WTD ACHD</th>
                        <th className="p-3.5 uppercase text-center border-r border-slate-800 bg-slate-900">WTD %</th>
                        {/* Monthly Group: Target -> Achieved -> % */}
                        <th className="p-3.5 uppercase text-center border-r border-slate-800 bg-slate-900">MONTH<br />TARGET</th>
                        <th className="p-3.5 uppercase text-center border-r border-slate-800 bg-slate-900">EC (VALUE)<br />MTD ACHD</th>
                        <th className="p-3.5 uppercase text-center border-r border-slate-800 bg-slate-900">MTD %</th>
                        {/* Yearly Group: Target -> Achieved -> % */}
                        <th className="p-3.5 uppercase text-center border-r border-slate-800 bg-slate-900">YEAR<br />TARGET</th>
                        <th className="p-3.5 uppercase text-center border-r border-slate-800 bg-slate-900">EC (VALUE)<br />ACHD</th>
                        <th className="p-3.5 uppercase text-center">YEAR ACHD %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 bg-white font-medium text-slate-700">
                      {matrixRows.map((row, idx) => (
                        <tr key={row.committeeName} className={idx % 2 === 0 ? 'bg-white hover:bg-slate-50' : 'bg-slate-50/50 hover:bg-slate-50'}>
                          <td className="p-3.5 border-r border-slate-200 font-bold text-slate-900">
                            <div>{row.committeeName}</div>
                            {row.heads && (
                              <div className="text-[10px] font-bold text-blue-700 mt-0.5 uppercase tracking-wide">
                                Heads: <span className="font-semibold text-slate-700">{row.heads}</span>
                              </div>
                            )}
                            {!row.leaders && !row.heads && row.leads && (
                              <div className="text-[10px] font-semibold text-blue-600 mt-0.5 uppercase tracking-wide">
                                {row.leads}
                              </div>
                            )}
                          </td>
                          {/* Weekly */}
                          <td className="p-3.5 text-center border-r border-slate-200">
                            <AmountDisplay
                              amount={row.weekTarget || 0}
                              label={`${row.committeeName} Week Target`}
                              textSize="text-xs font-semibold text-slate-500"
                              forceCrores={true}
                            />
                          </td>
                          <td className="p-3.5 text-center border-r border-slate-200">
                            <AmountDisplay
                              amount={row.wtdAchieved ?? Math.round((row.wtdAchievedCr || 0) * 10000000)}
                              label={`${row.committeeName} WTD Achieved`}
                              textSize="text-xs font-semibold"
                              forceCrores={true}
                            />
                          </td>
                          <td className="p-3.5 text-center border-r border-slate-200 font-bold" title={`${row.wtdPct}%`}>
                            {formatPctDisplay(row.wtdPct)}
                          </td>
                          {/* Monthly */}
                          <td className="p-3.5 text-center border-r border-slate-200">
                            <AmountDisplay
                              amount={row.monthTarget || 0}
                              label={`${row.committeeName} Month Target`}
                              textSize="text-xs font-semibold text-slate-500"
                              forceCrores={true}
                            />
                          </td>
                          <td className="p-3.5 text-center border-r border-slate-200">
                            <AmountDisplay
                              amount={row.mtdAchieved ?? Math.round((row.mtdAchievedCr || 0) * 10000000)}
                              label={`${row.committeeName} MTD Achieved`}
                              textSize="text-xs font-semibold"
                              forceCrores={true}
                            />
                          </td>
                          <td className="p-3.5 text-center border-r border-slate-200 font-bold" title={`${row.mtdPct}%`}>
                            {formatPctDisplay(row.mtdPct)}
                          </td>
                          {/* Yearly */}
                          <td className="p-3.5 text-center border-r border-slate-200">
                            <AmountDisplay
                              amount={row.yearTarget || 0}
                              label={`${row.committeeName} Year Target`}
                              textSize="text-xs font-semibold text-slate-500"
                              forceCrores={true}
                            />
                          </td>
                          <td className="p-3.5 text-center border-r border-slate-200 font-bold text-emerald-700">
                            <AmountDisplay
                              amount={row.ytdAchieved ?? Math.round((row.ytdAchievedCr || 0) * 10000000)}
                              label={`${row.committeeName} YTD Achieved`}
                              textSize="text-xs font-bold text-emerald-700"
                              forceCrores={true}
                            />
                          </td>
                          <td className="p-3.5 text-center font-bold text-emerald-700" title={`${row.yearAchdPct}%`}>
                            {formatPctDisplay(row.yearAchdPct)}
                          </td>
                        </tr>
                      ))}

                      {/* TOTAL Row */}
                      {matrixTotals && (
                        <tr className="bg-slate-100 text-slate-900 font-black border-t-2 border-slate-300">
                          <td className="p-3.5 border-r border-slate-300 uppercase tracking-wider text-blue-950">TOTAL</td>
                          {/* Weekly Totals */}
                          <td className="p-3.5 text-center border-r border-slate-300">
                            <AmountDisplay
                              amount={matrixTotals.weekTarget || 0}
                              label="Total Week Target"
                              textSize="text-xs font-bold text-slate-700"
                              forceCrores={true}
                            />
                          </td>
                          <td className="p-3.5 text-center border-r border-slate-300 text-blue-600">
                            <AmountDisplay
                              amount={matrixTotals.wtdAchieved ?? Math.round((matrixTotals.wtdAchievedCr || 0) * 10000000)}
                              label="Total WTD Achieved"
                              textSize="text-xs font-bold"
                              forceCrores={true}
                            />
                          </td>
                          <td className="p-3.5 text-center border-r border-slate-300 text-blue-600 font-black" title={`${matrixTotals.wtdPct}%`}>
                            {formatPctDisplay(matrixTotals.wtdPct)}
                          </td>
                          {/* Monthly Totals */}
                          <td className="p-3.5 text-center border-r border-slate-300">
                            <AmountDisplay
                              amount={matrixTotals.monthTarget || 0}
                              label="Total Month Target"
                              textSize="text-xs font-bold text-slate-700"
                              forceCrores={true}
                            />
                          </td>
                          <td className="p-3.5 text-center border-r border-slate-300 text-blue-600">
                            <AmountDisplay
                              amount={matrixTotals.mtdAchieved ?? Math.round((matrixTotals.mtdAchievedCr || 0) * 10000000)}
                              label="Total MTD Achieved"
                              textSize="text-xs font-bold"
                              forceCrores={true}
                            />
                          </td>
                          <td className="p-3.5 text-center border-r border-slate-300 text-blue-600 font-black" title={`${matrixTotals.mtdPct}%`}>
                            {formatPctDisplay(matrixTotals.mtdPct)}
                          </td>
                          {/* Yearly Totals */}
                          <td className="p-3.5 text-center border-r border-slate-300">
                            <AmountDisplay
                              amount={matrixTotals.yearTarget || 0}
                              label="Total Year Target"
                              textSize="text-xs font-bold text-slate-700"
                              forceCrores={true}
                            />
                          </td>
                          <td className="p-3.5 text-center border-r border-slate-300 text-emerald-700 font-black">
                            <AmountDisplay
                              amount={matrixTotals.ytdAchieved ?? Math.round((matrixTotals.ytdAchievedCr || 0) * 10000000)}
                              label="Total YTD Achieved"
                              textSize="text-xs font-black text-emerald-700"
                              forceCrores={true}
                            />
                          </td>
                          <td className="p-3.5 text-center text-emerald-700 font-black" title={`${matrixTotals.yearAchdPct}%`}>
                            {formatPctDisplay(matrixTotals.yearAchdPct)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
